import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { toast } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend, useTradeNotifications } from '@/features/wallet';
import type {
    TradeNotificationExecutionPhase,
    TradeNotificationUpdate,
    TradeStatus,
} from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import {
    findSlotByTradeHash,
    selectIsRealMode,
    useTradingStore,
} from '../store';
import type { CloseReason } from '../types';
import { consumeLocalLimitCancel } from '../utils/localLimitCancels';

import { useTradeCloseVisuals } from './useTradeCloseVisuals';

/**
 * A single open/close produces a burst of pushes (entry, then the tp/sl trigger rows),
 * and each one invalidates the same query. Coalesce them into one refetch — react-query
 * refetches an active query on every invalidate regardless of `staleTime`, so without
 * this the push path would reproduce the polling it replaces.
 */
const INVALIDATE_DEBOUNCE_MS = 120;

/**
 * What to say when a market the user is NOT looking at closes itself.
 *
 * The chart-anchored POSITION CLOSED card cannot serve this: it is drawn on the
 * charted market's chart, so playing it for another market would label one
 * market's outcome with another's price line. A named toast is the honest
 * surface — and under Option A ("the rail follows the chart") it is the only
 * warning a user gets that something happened off-screen.
 */
const OFF_CHART_OUTCOME: Partial<
    Record<TradeStatus, { text: string; bad: boolean }>
> = {
    Liquidated: { text: 'was liquidated', bad: true },
    StopLoss: { text: 'hit its stop loss', bad: true },
    TakeProfit: { text: 'hit its take profit', bad: false },
    Closed: { text: 'closed', bad: false },
};

/** statuses that move a trade out of the active list and into history. */
const TERMINAL_STATUSES = new Set<TradeStatus>([
    'Closed',
    'TakeProfit',
    'StopLoss',
    'Liquidated',
    'Failed',
    // a limit order dropped before it ever filled. Terminal, but NOT a close:
    // no position existed, so no close card, no sound, no PnL.
    'Cancelled',
]);

interface UseTradeNotificationHandlerParams {
    applyTradeUpdate: (
        tradeHash: string,
        status: TradeStatus,
        entryPrice?: number,
        /** only carried on a `Resting` push. */
        limitPrice?: string,
    ) => void;
    applyExecutionPhase: (update: TradeNotificationExecutionPhase) => void;
}

/**
 * Encapsulates the WebSocket trade notification pipeline:
 * - `handleWsTradeUpdate`: maps server status → close-visuals + state update
 * - `handleWsReconnect`: REST reconciliation after WS reconnect
 * - `useTradeNotifications`: the sole call site for that hook
 *
 * Reads all trading state from the store; only `applyTradeUpdate` is passed
 * because it's a method on the trade execution hook instance.
 */
export function useTradeNotificationHandler({
    applyTradeUpdate,
    applyExecutionPhase,
}: UseTradeNotificationHandlerParams): void {
    const networkId = useTokenPairStore((s) => s.networkId);
    const token = useTradingStore((s) => s.token);
    const isRealMode = useTradingStore(selectIsRealMode);
    // trades history moved to factions.backend's speed facet (read-only, V1).
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    const { fireCloseVisuals } = useTradeCloseVisuals();
    const setTradeStreamConnected = useTradingStore(
        (s) => s.setTradeStreamConnected,
    );

    // debounced refresh of the active-trades list. The push is what drives that query now
    // (it used to poll once a second); every update refreshes it, not just the managed
    // trade, because an unmanaged trade opening or closing changes the same list.
    const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const historyDirtyRef = useRef(false);
    const scheduleActiveTradesRefresh = useCallback(
        (alsoHistory = false) => {
            if (alsoHistory) historyDirtyRef.current = true;
            if (invalidateTimerRef.current) return;
            invalidateTimerRef.current = setTimeout(() => {
                invalidateTimerRef.current = null;
                const refreshHistory = historyDirtyRef.current;
                historyDirtyRef.current = false;
                const networkId = useTokenPairStore.getState().networkId;
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.active(networkId ?? 0),
                });
                // any status change can create or clear untracked exposure (a
                // partial fill, a close that left a remainder), so the warning
                // panel refreshes on the same debounced beat as the list.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.exposure(networkId ?? 0),
                });
                // only a trade reaching a terminal status can change history — refreshing it
                // on every Pending→Open too would put back half the requests this removed.
                if (refreshHistory) {
                    void queryClient.invalidateQueries({
                        queryKey: queryKeys.trades.history(networkId ?? 0),
                    });
                }
            }, INVALIDATE_DEBOUNCE_MS);
        },
        [queryClient],
    );

    useEffect(
        () => () => {
            if (invalidateTimerRef.current) {
                clearTimeout(invalidateTimerRef.current);
                invalidateTimerRef.current = null;
            }
        },
        [],
    );

    const handleWsTradeUpdate = useCallback(
        (update: TradeNotificationUpdate) => {
            const closeReasonMap: Partial<Record<TradeStatus, CloseReason>> = {
                Closed: 'manual',
                TakeProfit: 'TakeProfit',
                StopLoss: 'StopLoss',
                Liquidated: 'Liquidated',
            };

            const closeReason = closeReasonMap[update.status];
            const s = useTradingStore.getState();

            // ── Limit-order outcomes ─────────────────────────────────────
            // Both are announced by a plain toast rather than the close card:
            // the card is anchored to a position's entry line, and a resting
            // order has no position to anchor to.
            const restingHit = findSlotByTradeHash(s, update.tradeHash);
            if (
                update.status === 'Open' &&
                restingHit?.slot.status === 'resting'
            ) {
                toast.success('Limit order filled, position is running', {
                    duration: 8_000,
                });
            }
            if (update.status === 'Cancelled') {
                // the tab that pressed Cancel already said so — don't say it twice
                const wasLocal = consumeLocalLimitCancel(update.tradeHash);
                if (!wasLocal && restingHit !== null) {
                    toast.success(`${restingHit.coin} limit order cancelled`, {
                        duration: 8_000,
                    });
                }
                // the slot holds a resting order, not a position, so the close
                // path below is wrong for it: drop it directly.
                useTradingStore.getState()._closeSlotForTrade(update.tradeHash);
            }
            if (closeReason) {
                // Route the outcome to the market that actually closed, found by
                // the trade's own hash. Comparing against `s.tradeHash` — the
                // CHARTED market's trade — meant a stop-loss or liquidation on
                // any other market produced nothing at all.
                const hit = findSlotByTradeHash(s, update.tradeHash);
                const isCharted = hit !== null && hit.coin === s.selectedCoin;

                if (
                    isCharted &&
                    s.execEntryPrice !== null &&
                    s.execPositionType &&
                    s.execPositionAmount !== null &&
                    s.tradeHash
                ) {
                    // the chart-anchored card, for the market on the chart
                    fireCloseVisuals({
                        entry: s.execEntryPrice,
                        exitPrice: s.currentValue,
                        posType: s.execPositionType,
                        posAmount: s.execPositionAmount,
                        leverage: s.execLeverage ?? 1,
                        closeReason,
                        tradeHash: s.tradeHash,
                    });
                } else if (hit !== null) {
                    const outcome = OFF_CHART_OUTCOME[update.status];
                    if (outcome) {
                        const message = `${hit.coin} ${outcome.text}`;
                        // a liquidation or stop is not a success, and must not
                        // read as one just because it happened out of sight.
                        if (outcome.bad) {
                            toast.error(message, { duration: 12_000 });
                        } else {
                            toast.success(message, { duration: 8_000 });
                        }
                    }
                }
            }

            if (closeReason) {
                // ...and clear the position itself. applyTradeUpdate below routes
                // by the slots THIS tab is executing, so a restored trade — one
                // opened before a reload, or in another tab — never reached it
                // and stayed on the chart. The store knows about those.
                useTradingStore.getState()._closeSlotForTrade(update.tradeHash);
            }

            const wsEntry =
                update.entryPx !== undefined && update.entryPx !== ''
                    ? Number(update.entryPx)
                    : undefined;
            applyTradeUpdate(
                update.tradeHash,
                update.status,
                Number.isFinite(wsEntry) ? wsEntry : undefined,
                update.limitPrice,
            );

            // any trade changing status changes the active list — including one this tab
            // isn't managing — so this is deliberately not gated on tradeHash. A terminal
            // status additionally moves the row into history.
            scheduleActiveTradesRefresh(TERMINAL_STATUSES.has(update.status));

            // The push carries the live position (liq price, uPnL, margin) on
            // the Pending→Open transition; refresh the authoritative position
            // reads so the panel/chart pick those up immediately rather than
            // waiting for the next poll.
            //
            // Not gated on the charted market: this invalidates the whole
            // position key (every coin) plus the bulk read, and a market the
            // user is not watching needs its figures refreshed just as much —
            // that is what the positions table and the liquidation banner show.
            void queryClient.invalidateQueries({
                queryKey: [...queryKeys.speed.all, 'position'],
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.positions(),
            });
        },
        [
            applyTradeUpdate,
            fireCloseVisuals,
            queryClient,
            scheduleActiveTradesRefresh,
        ],
    );

    const handleWsReconnect = useCallback(() => {
        const s = useTradingStore.getState();
        // updates that landed while the socket was down were missed entirely, and any of
        // them could have been terminal, so refresh both lists on every reconnect — not
        // only when this tab manages a trade.
        scheduleActiveTradesRefresh(true);
        if (!backend) return;

        // Every market this client is tracking, not just the charted one. The
        // old guard bailed on `!s.tradeHash` — the CHARTED market's trade — so
        // reconnecting while looking at a market with no position skipped the
        // reconciliation entirely, and even when it ran it matched exactly one
        // hash out of the list and discarded the rest.
        const tracked = Object.values(s.positionsByCoin)
            .filter(
                (slot) =>
                    slot.tradeHash !== null &&
                    (slot.status === 'pending' ||
                        slot.status === 'open' ||
                        slot.status === 'closing' ||
                        // a resting order can have filled OR been cancelled
                        // while the socket was down, and both pushes are gone
                        slot.status === 'resting'),
            )
            .map((slot) => slot.tradeHash as string);
        if (tracked.length === 0) return;

        const trackedHashes = new Set(tracked);
        const nid = useTokenPairStore.getState().networkId;
        backend
            .fetchUserTrades({
                networkId: nid,
                status: 'Open,Pending,Resting,Closed,TakeProfit,StopLoss,Liquidated,Failed,Cancelled',
                // enough headroom that N tracked markets plus the closes that
                // landed while the socket was down all fit in one page.
                limit: 50,
            })
            .then((trades) => {
                for (const trade of trades) {
                    if (!trackedHashes.has(trade.tradeHash)) continue;
                    const entry = Number(trade.entryPrice);
                    applyTradeUpdate(
                        trade.tradeHash,
                        trade.status,
                        trade.entryPrice !== null && Number.isFinite(entry)
                            ? entry
                            : undefined,
                        trade.limitPrice ?? undefined,
                    );
                }
            })
            .catch((err: unknown) => {
                console.error('[Trading] Reconciliation fetch failed:', err);
            });
    }, [backend, applyTradeUpdate, scheduleActiveTradesRefresh]);

    const { isConnected } = useTradeNotifications({
        networkId,
        token: isRealMode ? token : null,
        onTradeUpdate: handleWsTradeUpdate,
        onExecutionPhase: applyExecutionPhase,
        onReconnect: handleWsReconnect,
    });

    // published so the active-trades query knows whether updates are arriving by push and
    // can hold off polling. Reported as disconnected on unmount so a torn-down stream is
    // never mistaken for a live one.
    useEffect(() => {
        setTradeStreamConnected(isConnected);
    }, [isConnected, setTradeStreamConnected]);

    useEffect(
        () => () => {
            setTradeStreamConnected(false);
        },
        [setTradeStreamConnected],
    );
}
